import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { TranscriptEntry, TranscriptSpeaker } from "@/modules/speech-to-text/types"

export type TranscriptDisplayProps = {
  transcripts: TranscriptEntry[]
  isRecording: boolean
  className?: string
}

const getSpeakerStyles = (speaker: TranscriptSpeaker) => {
  if (speaker === "AGENT") {
    return {
      container: "border-l-primary bg-primary-surface-subdued border-l-4",
      label: "bg-primary-surface text-on-primary-surface",
      text: "text-on-primary-surface",
    }
  }
  return {
    container: "border-l-success bg-success-surface-subdued border-l-4",
    label: "bg-success-surface text-on-success-surface",
    text: "text-on-success-surface",
  }
}

export const TranscriptDisplay = ({ transcripts, isRecording, className = "" }: TranscriptDisplayProps) => {
  const { t } = useTranslation()
  const transcriptsEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to latest transcript
  useEffect(() => {
    transcriptsEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [transcripts])

  return (
    <div className={`flex h-full flex-col ${className}`}>
      {/* Header with recording status */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-on-surface text-lg font-semibold">{t("speechToText.transcriptTitle")}</h3>
        {isRecording && (
          <div className="flex items-center gap-2">
            <div className="bg-success size-2 animate-pulse rounded-full"></div>
            <span className="text-success text-sm font-medium">{t("speechToText.recording")}</span>
          </div>
        )}
      </div>

      {/* Transcripts list */}
      <div className="bg-background-subdued flex-1 overflow-y-auto p-4">
        {transcripts.length === 0 ? (
          <p className="text-on-surface-subdued italic">{t("speechToText.noTranscripts")}</p>
        ) : (
          <div className="space-y-3">
            {transcripts.map((transcript) => {
              const styles = getSpeakerStyles(transcript.speaker)
              return (
                <div key={transcript.id} className={`rounded-lg p-3 shadow-sm ${styles.container}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles.label}`}>
                        {transcript.speaker === "AGENT"
                          ? t("speechToText.speakerAgent")
                          : t("speechToText.speakerPatient")}
                      </span>
                      <span className="text-on-surface-subdued text-xs">
                        {new Date(transcript.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    {transcript.isFinal && (
                      <span className="bg-surface-subdued text-on-surface-subdued rounded-full px-2 py-0.5 text-xs font-medium">
                        {t("speechToText.finalTranscript")}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${styles.text}`}>{transcript.text}</p>
                </div>
              )
            })}
            <div ref={transcriptsEndRef} />
          </div>
        )}
      </div>
    </div>
  )
}
